import axios from "axios";
import { diagApi, currentInteraction } from "@/lib/diag";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE, withCredentials: true });

api.interceptors.request.use((config) => {
  config.metadata = { t0: performance.now() };
  const ix = currentInteraction();                        // diagnostics-only correlation id; never auth, identity or idempotency
  if (ix) { config.headers["X-Interaction-Id"] = ix.cid; config.metadata.cid = ix.cid; config.metadata.mod = ix.mod; }
  const token = localStorage.getItem("neec_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Register identity travels in an HttpOnly cookie; header is a fallback for browsers that dropped the cookie
  // (and for legacy Quick Login terminals whose trusted-device secret was migrated into a register).
  const reg = localStorage.getItem("neec_register_token") || localStorage.getItem("neec_device_token");
  if (reg) config.headers["X-Register-Token"] = reg;
  return config;
});

// Watchtower V2 Slice 2 (Checkout): path check + fixed, read-only reason classification from response shapes that
// already exist today (never guessed, never derived from payment/credential fields). Scoped to /sales and
// /sales/offline only — every other module's existing classification below is unchanged.
const isCheckoutPath = (cfg) => /^\/sales(\/|$)/.test(String(cfg?.url || ""));
const checkoutReason = (cfg, status, ok, data, detail, headers) => {
  if (/\/sales\/offline/.test(String(cfg?.url || ""))) {
    if (!ok) return (status === 409 || status >= 500) ? "offline_sync_pending_retry" : "offline_sync_failed";
    return data?.replayed ? "offline_sync_replayed" : ((data?.exceptions || []).length ? "offline_synced_with_exceptions" : "offline_synced");
  }
  if (ok) return headers?.["idempotent-replay"] === "true" ? "duplicate_replayed" : (data?.post_commit_warning ? "checkout_ok_with_warning" : "checkout_ok");
  if (status >= 500) return "server_error";
  if (!detail || typeof detail !== "object") return null;   // unmapped 4xx -> no reason -> never excluded from failure scoring
  if (detail.code === "credit_limit_concurrent") return "recovery_compensated";
  if (detail.code === "sale_recovery_pending") return "recovery_pending";
  if (detail.code === "duplicate_in_flight" || detail.code === "idempotency_key_reused") return "duplicate_blocked";
  if (detail.insufficient_stock) return "rejected_stock";
  if (detail.shift_required) return "rejected_shift";
  if (detail.needs_approval) return "rejected_approval";
  if (detail.reason === "overpayment" || ["variant_required", "department_archived", "prepaid_insufficient", "customer_closed", "feature_disabled"].includes(detail.code)) return "rejected_payment";
  return null;
};

const observe = (cfg, status, failed, msg, reason) => { try { diagApi((cfg?.method || "get").toUpperCase(), `${cfg?.baseURL || ""}${cfg?.url || ""}`, status, performance.now() - (cfg?.metadata?.t0 ?? performance.now()), failed, msg, cfg?.metadata?.cid, cfg?.metadata?.mod, reason); } catch { /* never */ } };
api.interceptors.response.use(
  (r) => { observe(r.config, r.status, false, undefined, isCheckoutPath(r.config) ? checkoutReason(r.config, r.status, true, r.data, null, r.headers) : undefined); return r; },
  (err) => {
    const detail = err.response?.data?.detail;
    const checkout = isCheckoutPath(err.config);
    const reason = checkout ? (err.response ? checkoutReason(err.config, err.response.status, false, err.response.data, detail, err.response.headers) : "network_error_unknown") : undefined;
    // Only /sales and /sales/offline additionally treat a 4xx/409 as a failure event (so it can carry its reason);
    // every other module's existing "failed only on no-response or 5xx" behaviour is completely unchanged.
    const checkoutFail = checkout && !!err.response && err.response.status >= 400 && err.response.status < 500;
    observe(err.config, err.response?.status || 0, !err.response || err.response.status >= 500 || checkoutFail, err.response ? undefined : (err.code || err.message), reason);
    if (!err.response && err.code !== "ECONNABORTED" && !String(err.config?.url || "").includes("/health")) {
      // Network failure (not an HTTP error): let the connectivity manager flip to OFFLINE immediately.
      import("@/lib/offline/connectivity").then((m) => m.reportNetworkError()).catch(() => {});
    }
    if (err.response?.status === 401 && !window.location.pathname.includes("/login")) {
      localStorage.removeItem("neec_token");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export function apiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof detail === "object" && detail.message) return detail.message;
  return String(detail);
}

export default api;
